/**
 * Review Traveller API
 *
 * Network-only functions for review traveller form operations.
 * NO Redux imports allowed in this file.
 *
 * Uses common flights API utilities which automatically add:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 */
import * as v from 'valibot';
import { FLIGHTS_ENDPOINTS } from '@/api/endpoints';
import { buildQueryParams, buildHeaders, buildUrl } from '@/api/utils';
import { logger } from '@/shared/utils/logger';
import type { TravellerFormRequestParams, ValidateTravellerResponse } from '../types';
import {
  type ValidationResponse as ValidateTravellerInfoResponse,
  ValidationResponseSchema,
  type ValidateTravellerFormRequestParams,
  type ValidateTravellerInfoRequestParams,
  type FlightsReviewTravellersTemplate,
  type ApiErrorResponse,
  type PopupErrorDetails,
} from '@contracts';
import { extractPopupErrorDetails } from '@/features/flights/utils/apiErrorUtils';

// ============================================
// Error Types
// ============================================

/**
 * Structured error details from API response.
 * Used for displaying error popups with meaningful messages.
 * @deprecated Use PopupErrorDetails from @contracts instead
 */
export type ReviewTravellerErrorDetails = PopupErrorDetails;

// ============================================
// Error Classes
// ============================================

export class ReviewTravellerError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly cause?: unknown,
    public readonly errorDetails?: PopupErrorDetails,
  ) {
    super(message);
    this.name = 'ReviewTravellerError';
  }
}

export class ReviewTravellerValidationError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'ReviewTravellerValidationError';
  }
}

export class ValidateTravellerInfoError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'ValidateTravellerInfoError';
  }
}

// Valibot schema for validation
const ValidateTravellerFormResponseSchema = v.object({
  status: v.string(),
  data: v.optional(v.unknown()),
});

// ============================================
// API Functions
// ============================================

/**
 * Fetch review traveller form details
 *
 * Uses common flights API wrapper with automatic:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 */
export async function fetchReviewTravellers(
  params: TravellerFormRequestParams,
): Promise<FlightsReviewTravellersTemplate> {
  const queryParams = buildQueryParams({
    crId: params.crId,
    rKey: params.contextHashKey ?? '',
    itineraryId: params.itineraryId ?? '',
    currency: params.currency ?? 'INR',
    language: params.language ?? 'eng',
    region: params.region ?? 'in',
    cc: params.cabinClass ?? 'E',
  });
  const url = buildUrl(FLIGHTS_ENDPOINTS.REVIEW_TRAVELLER, queryParams);
  const headers = buildHeaders();

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    // Always try to parse response body to get error details
    const data = (await response.json()) as FlightsReviewTravellersTemplate & ApiErrorResponse;

    // Check if API returned an error in the response body
    const errorDetails = extractPopupErrorDetails(data);
    if (errorDetails) {
      throw new ReviewTravellerError(
        errorDetails.errorMessage,
        response.status,
        data,
        errorDetails,
      );
    }

    if (!response.ok) {
      throw new ReviewTravellerError(
        `API request failed: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    logger.warn('[ReviewTravellerClient] Received template:', data);
    return data;
  } catch (error) {
    if (error instanceof ReviewTravellerError) {
      throw error;
    }

    logger.error('[ReviewTravellerClient] Request failed:', error);
    throw new ReviewTravellerError('Failed to fetch review traveller details', undefined, error);
  }
}

/**
 * Server error response structure for field validation errors
 * Format:
 * {
 *   "type": "error",
 *   "isValid": false,
 *   "errors": {
 *     "contactDetailsForm": { "rowFields": { "EMAIL": "error message", ... } },
 *     "gstDetailsForm": { "rowFields": { "REGISTRATION_NO": "error message", ... } }
 *   }
 * }
 */
interface ServerValidationErrorResponse {
  type?: string;
  isValid?: boolean;
  errors?: {
    contactDetailsForm?: {
      rowFields?: Record<string, string>;
    };
    gstDetailsForm?: {
      rowFields?: Record<string, string>;
    };
  };
}

/**
 * Extract field errors from server error response
 */
function extractFieldErrors(responseData: unknown): ValidateTravellerResponse['fieldErrors'] {
  if (!responseData || typeof responseData !== 'object') {
    return undefined;
  }

  const errorResponse = responseData as ServerValidationErrorResponse;

  if (!errorResponse.errors) {
    return undefined;
  }

  const fieldErrors: ValidateTravellerResponse['fieldErrors'] = {};

  // Extract contact details field errors
  if (errorResponse.errors.contactDetailsForm?.rowFields) {
    fieldErrors.contactDetailsForm = {
      rowFields: errorResponse.errors.contactDetailsForm.rowFields,
    };
  }

  // Extract GST details field errors
  if (errorResponse.errors.gstDetailsForm?.rowFields) {
    fieldErrors.gstDetailsForm = {
      rowFields: errorResponse.errors.gstDetailsForm.rowFields,
    };
  }

  return Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined;
}

/**
 * Validate traveller form
 *
 * Uses common flights API utilities with automatic:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 */
export async function validateTravellerForm(
  params: ValidateTravellerFormRequestParams,
): Promise<ValidateTravellerResponse> {
  const queryParams = buildQueryParams({
    crId: params.crId,
    currency: params.currency ?? 'INR',
    language: params.language ?? 'eng',
    region: params.region ?? 'in',
    cc: params.cabinClass ?? 'E',
    itineraryId: params.itineraryId,
  });
  const url = buildUrl(FLIGHTS_ENDPOINTS.VALIDATE_TRAVELLER_FORM, queryParams);
  const headers = buildHeaders();

  logger.warn('[ReviewTravellerClient] Making API request to:', url);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(params.requestBody),
    });

    const responseData = (await response.json()) as unknown;
    logger.warn('[ReviewTravellerClient] Raw API response:', responseData);

    try {
      const validatedResponse = v.parse(ValidateTravellerFormResponseSchema, responseData);

      if (validatedResponse.status === 'RESPONSE_STATUS_SUCCESS') {
        return { status: 'RESPONSE_STATUS_SUCCESS' };
      }

      // Extract field errors from error response
      const fieldErrors = extractFieldErrors(responseData);

      return {
        status: 'VALIDATION_ERROR',
        errorMessage: 'Validation failed',
        fieldErrors,
      };
    } catch (validationError) {
      // Even if schema validation fails, try to extract field errors
      // This handles cases where the error response has a different structure
      const fieldErrors = extractFieldErrors(responseData);

      if (fieldErrors) {
        logger.warn('[ReviewTravellerClient] Extracted field errors from response:', fieldErrors);
        return {
          status: 'VALIDATION_ERROR',
          errorMessage: 'Field validation failed',
          fieldErrors,
        };
      }

      logger.error('[ReviewTravellerClient] Validation failed:', validationError);
      throw new ReviewTravellerValidationError(
        'Invalid review traveller validation response structure',
        response.status,
        validationError,
      );
    }
  } catch (error) {
    if (error instanceof ReviewTravellerValidationError) {
      throw error;
    }

    const message =
      error instanceof Error
        ? `Failed to validate traveller form: ${error.message}`
        : 'Failed to validate traveller form: Unknown error';

    throw new ReviewTravellerValidationError(message, undefined, error);
  }
}

/**
 * Validate single traveller information before saving
 * Used when adding/editing a traveller in the modal
 *
 * Uses common flights API utilities with automatic:
 * - Headers: X-Gommt-Mmt-Auth, X-Gommt-Uuid, X-Device-Id
 * - Query params: sessionId, userId
 *
 * @param params - Request parameters including crId, itineraryId, and traveller data
 * @returns ValidateTravellerInfoResponse - Contains either validated traveller or validation errors
 * @throws ValidateTravellerInfoError if the request fails
 */
export async function validateTravellerInfo(
  params: ValidateTravellerInfoRequestParams,
): Promise<ValidateTravellerInfoResponse> {
  const queryParams = buildQueryParams({
    crId: params.crId,
    itineraryId: params.itineraryId,
  });
  const url = buildUrl(FLIGHTS_ENDPOINTS.VALIDATE_TRAVELLER_INFO, queryParams);
  const headers = buildHeaders();

  logger.warn('[ValidateTravellerInfoClient] Making API request to:', url);
  logger.warn('[ValidateTravellerInfoClient] Request body:', params.requestBody);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(params.requestBody),
    });

    const responseData = (await response.json()) as unknown;
    logger.warn('[ValidateTravellerInfoClient] Raw API response:', responseData);

    // Check for HTTP errors (non-2xx status) that aren't validation responses
    if (!response.ok) {
      // Try to parse as validation response (could be error type)
      try {
        const validationResponse = v.parse(ValidationResponseSchema, responseData);
        logger.warn('[ValidateTravellerInfoClient] Validation response:', validationResponse);
        return validationResponse;
      } catch {
        throw new ValidateTravellerInfoError(
          `Validate traveller info request failed with status ${response.status}`,
          response.status,
          responseData,
        );
      }
    }

    // Parse response using discriminated union schema (handles success, error, and airline_rules)
    try {
      const validationResponse = v.parse(ValidationResponseSchema, responseData);
      logger.warn('[ValidateTravellerInfoClient] Validated response:', validationResponse);
      return validationResponse;
    } catch (validationError) {
      logger.error('[ValidateTravellerInfoClient] Response validation failed:', validationError);

      // Extract detailed validation error information
      let errorMessage = 'Invalid validate-traveller-info response structure';
      if (validationError instanceof v.ValiError) {
        const issueDetails = validationError.issues
          .map((issue) => {
            const path =
              issue.path?.map((p: { key: string | number }) => p.key).join('.') ?? 'root';
            return `  - Path: "${path}"\n    Expected: ${issue.expected}\n    Received: ${issue.received}\n    Message: ${issue.message}`;
          })
          .join('\n\n');

        errorMessage = `Validation failed with ${validationError.issues.length} issue(s):\n\n${issueDetails}`;
        logger.error('[ValidateTravellerInfoClient] Detailed validation errors:\n', issueDetails);
      }

      throw new ValidateTravellerInfoError(errorMessage, response.status, validationError);
    }
  } catch (error) {
    // Handle fetch errors
    if (error instanceof ValidateTravellerInfoError) {
      throw error;
    }

    // Network or other errors
    const message =
      error instanceof Error
        ? `Failed to validate traveller info: ${error.message}`
        : 'Failed to validate traveller info: Unknown error';

    throw new ValidateTravellerInfoError(message, undefined, error);
  }
}

// ============================================
// Backward Compatibility Aliases (Deprecated)
// ============================================

/**
 * @deprecated Use ReviewTravellerError instead
 */
export const TravellerFormError = ReviewTravellerError;

/**
 * @deprecated Use ReviewTravellerValidationError instead
 */
export const TravellerFormValidationError = ReviewTravellerValidationError;

/**
 * @deprecated Use fetchReviewTravellers instead
 */
export const fetchTravellerFormDetails = fetchReviewTravellers;
